import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import agent from '../api/axios';

interface Payee {
  id: string;
  firstName: string;
  lastName: string;
  companyName?: string;
  email?: string;
  phone?: string;
  phoneNumber?: string;
  address?: string;
  streetAddress?: string;
  city?: string;
  state?: string;
  zip?: string;
  zipCode?: string;
  tin?: string;
  isActive: boolean;
  hourlyRate?: number;
  payeeType?: string;
  createdDate?: string;
  updatedDate?: string;
}

interface PayeesState {
  payees: Payee[];
  loading: boolean;
  error: string | null;
}

const initialState: PayeesState = {
  payees: [],
  loading: false,
  error: null,
};

export const fetchPayees = createAsyncThunk('payees/fetchAll', async () => {
  const response = await agent.Employees.list();
  return response;
});

export const createPayee = createAsyncThunk(
  'payees/create',
  async (payee: Partial<Payee>) => {
    const response = await agent.Employees.add(payee as any);
    return response;
  }
);

export const updatePayee = createAsyncThunk(
  'payees/update',
  async ({ id, data }: { id: string; data: Partial<Payee> }) => {
    const response = await agent.Employees.update(id, data as any);
    return response;
  }
);

export const deletePayee = createAsyncThunk(
  'payees/delete',
  async (id: string) => {
    await agent.Employees.remove(id);
    return id;
  }
);

const payeesSlice = createSlice({
  name: 'payees',
  initialState,
  reducers: {
    clearPayees: (state) => {
      state.payees = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch payees
      .addCase(fetchPayees.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPayees.fulfilled, (state, action) => {
        state.loading = false;
        state.payees = action.payload;
      })
      .addCase(fetchPayees.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch payees';
      })
      // Create payee
      .addCase(createPayee.fulfilled, (state, action) => {
        state.payees.push(action.payload);
      })
      // Update payee
      .addCase(updatePayee.fulfilled, (state, action) => {
        const index = state.payees.findIndex((p) => p.id === action.payload.id);
        if (index !== -1) {
          state.payees[index] = action.payload;
        }
      })
      // Delete payee
      .addCase(deletePayee.fulfilled, (state, action) => {
        state.payees = state.payees.filter((p) => p.id !== action.payload);
      });
  },
});

export const { clearPayees } = payeesSlice.actions;
export default payeesSlice.reducer;
